import { SplashScreenSdbTopic } from "@components/splash-screen/splash-screen-data";
import { SplashScreenViewStatus } from "@components/splash-screen/splash-screen-utils";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  changeAllSdbSplashScreensStatus,
  parseFueFlags,
} from "@store/user/user-slice-helper";
import {
  fetchCurrentUser,
  getLoggedInUser,
  logoutUser,
  setFueFlags,
  updateCurrentUserProfile,
} from "@store/user/user-slice-thunk";
import { FetchingStatus } from "@store/store-types";
import { AllSplashScreens } from "@store/user/user-slice-types";

/** State of User members */
export interface UserState {
  /** Current logged in user */
  currentUser: FaroCoreApiTypes.CompanyMemberBase | null;

  /** All the splash screens with their view status */
  splashScreens: AllSplashScreens;

  /**
   * Extra information about current logged in user.
   * For details like name, email, etc. use currentUser.
   * This is only used for information like login method, SSO provider, etc.
   */
  loggedInUser: FaroCoreApiTypes.LoggedInUser | null;

  /**
   * If the user requests to update their email, this will be set to the new email.
   * This is used because email is not changed automatically and should be validated first.
   * But if we don't show the new email in the current session, the user might think that the email was not changed.
   */
  updatedEmail: undefined | string;

  /** Collects all the fetching properties for this slice */
  fetching: {
    /** Flag whether the current user is being fetched from the backend */
    isFetchingCurrentUser: boolean;

    /** Status of fetching the logged in user */
    loggedInUserFetchingStatus: FetchingStatus;

    /** Flag whether the Fue flags are being set to the backend */
    isSettingFueFlags: boolean;

    /** Flag whether the user clicked on logging out, and the logout route is being called */
    isLoggingOut: boolean;
  };
}

const initialState: UserState = {
  currentUser: null,
  loggedInUser: null,

  // At initial state, all splash screens are unseen. The status will update based on the fetched data from backend
  splashScreens: { sdb: changeAllSdbSplashScreensStatus("unseen"), other: {} },

  updatedEmail: undefined,
  fetching: {
    isFetchingCurrentUser: false,
    loggedInUserFetchingStatus: "uninitialized",
    isSettingFueFlags: false,
    isLoggingOut: false,
  },
};

/**
 * Slice to access state of loaded user members
 */
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setCurrentUser(
      state,
      action: PayloadAction<FaroCoreApiTypes.CompanyMemberBase>
    ) {
      state.currentUser = action.payload;
    },

    setLoggedInUser(
      state,
      action: PayloadAction<FaroCoreApiTypes.LoggedInUser>
    ) {
      state.loggedInUser = action.payload;
    },

    setUpdatedEmail(state, action: PayloadAction<string>) {
      state.updatedEmail = action.payload;
    },

    /** Set the status of the splashscreen topic to the provided one */
    setSplashScreenStatus(
      state,
      action: PayloadAction<{
        topic: SplashScreenSdbTopic;
        status: SplashScreenViewStatus;
      }>
    ) {
      state.splashScreens.sdb[action.payload.topic] = action.payload.status;
    },

    /** Reset the status of all the splashscreen topics to unseen */
    resetSplashScreenStatus(state) {
      state.splashScreens.sdb = changeAllSdbSplashScreensStatus("unseen");
    },
    
    /** Set the fetching status for logged in user */
    setLoggedInUserFetchingStatus(state, action: PayloadAction<FetchingStatus>) {
      state.fetching.loggedInUserFetchingStatus = action.payload;
    },

    resetUserState: () => initialState,

  },
  extraReducers(builder) {
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.fetching.isFetchingCurrentUser = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.fetching.isFetchingCurrentUser = false;
        state.currentUser = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.fetching.isFetchingCurrentUser = false;
      })

      .addCase(getLoggedInUser.pending, (state) => {
        state.fetching.loggedInUserFetchingStatus = "pending";
      })
      .addCase(getLoggedInUser.fulfilled, (state, action) => {
        state.loggedInUser = action.payload;
        state.fetching.loggedInUserFetchingStatus = "succeeded";

        if (action.payload.fueFlags) {
          state.splashScreens = parseFueFlags(
            state.splashScreens,
            action.payload.fueFlags
          );
        }
      })
      .addCase(getLoggedInUser.rejected, (state) => {
        state.fetching.loggedInUserFetchingStatus = "rejected";
      })

      .addCase(setFueFlags.pending, (state) => {
        state.fetching.isSettingFueFlags = true;
      })
      .addCase(setFueFlags.fulfilled, (state, action) => {
        state.fetching.isSettingFueFlags = false;

        state.splashScreens = parseFueFlags(
          state.splashScreens,
          action.payload.updatedValue
        );
      })
      .addCase(setFueFlags.rejected, (state, _action) => {
        state.fetching.isSettingFueFlags = false;
      })

      .addCase(updateCurrentUserProfile.fulfilled, (state, action) => {
        if (state.currentUser) {
          state.currentUser = {
            ...state.currentUser,
            email: action.payload.mailAddress,
            firstName: action.payload.name,
            lastName: action.payload.lastName,
            thumbnailUrl: action.payload.profileImageUrl,
          };
        }
        if (state.loggedInUser) {
          state.loggedInUser = {
            ...state.loggedInUser,
            email: action.payload.mailAddress,
            firstName: action.payload.name,
            lastName: action.payload.lastName,
            thumbnailUrl: action.payload.profileImageUrl,
          };
        }
      })

      .addCase(logoutUser.pending, (state) => {
        state.fetching.isLoggingOut = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.fetching.isLoggingOut = false;
        state.loggedInUser = null;
      })
      .addCase(logoutUser.rejected, (state) => {
        state.fetching.isLoggingOut = false;
      });
  },
});

export const {
  setCurrentUser,
  setLoggedInUser,
  setUpdatedEmail,
  setSplashScreenStatus,
  resetSplashScreenStatus,
  resetUserState,
  setLoggedInUserFetchingStatus,
} = userSlice.actions;

export const userReducer = userSlice.reducer;
